import {
  ClockCircleOutlined,
  ControlOutlined,
  MailOutlined,
} from '@ant-design/icons'
import FormCard from '@components/FormCard'
import Page from '@components/Page/Page'
import { SettingKey } from '@models/Setting'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getSettings, selectSettingByKey } from '@store/SettingReducer'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import MetadataKey from '@utils/MetadataKey'
import { Permission } from '@utils/permissions'
import { emailRule } from '@utils/rules'
import { Alert, Col, Divider, Flex, Row, Spin } from 'antd'
import { useEffect } from 'react'

const BookingSettingsPage = () => {
  const dispatch = useAppDispatch()
  const { isLoading } = useAppSelector((state) => state.setting)

  const fetchSettings = () => {
    dispatch(
      getSettings([
        SettingKey.BOOKING_GENERIC_EMAIL,
        SettingKey.BOOKING_CANCELLED_STATUS,
        SettingKey.BOOKING_STATUS_TO_HIDE,
        SettingKey.BOOKING_IS_UPDATABLE_TIME,
        SettingKey.BOOKING_IS_CANCELLABLE_TIME,
      ])
    )
  }
  useEffect(() => {
    fetchSettings()
  }, [])

  const updateCutOff = useAppSelector(
    selectSettingByKey(SettingKey.BOOKING_IS_UPDATABLE_TIME)
  )
  const cancelCutOff = useAppSelector(
    selectSettingByKey(SettingKey.BOOKING_IS_CANCELLABLE_TIME)
  )
  return (
    <Page permissions={Permission.manageSettingsBookingSettings}>
      <Flex vertical gap={100}>
        <Flex vertical gap={0}>
          <PageTitle
            title="Booking statuses"
            level={4}
            style={{ margin: 0 }}
            toolbar={isLoading ? <Spin /> : undefined}
          />
          <Divider />
          <Row gutter={[32, 32]}>
            <Col span={12}>
              <FormCard
                title="Cancelled status"
                description={`When having this status, the Reservation is displayed as disabled - It's not possible to access its details. If the status is present in the "Status to hide" setting, the reservation won't be visible in app`}
                icon={<ControlOutlined />}
                entity={Entity.setting}
                action={SBRMAction.Update}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.BOOKING_CANCELLED_STATUS,
                  },
                  {
                    key: MetadataKey.ExtraProps,
                    value: {
                      label: 'Status',
                    },
                  },
                ]}
              />
            </Col>
            <Col span={12}>
              <FormCard
                title="Status to hide"
                description="Reservations with these statuses won't be displayed in the app. If empty, all reservations will be displayed"
                icon={<ControlOutlined />}
                entity={Entity.setting}
                action={SBRMAction.Update}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.BOOKING_STATUS_TO_HIDE,
                  },
                ]}
              />
            </Col>
            <Col span={12}>
              <FormCard
                title="Generic booking email"
                description="The generic booking email will be displayed after a max partize request for a venue when the venue group booking email is not set up."
                icon={<MailOutlined />}
                entity={Entity.setting}
                action={SBRMAction.Update}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.BOOKING_GENERIC_EMAIL,
                  },
                  {
                    key: MetadataKey.ExtraProps,
                    value: {
                      label: 'Email',
                      rules: [emailRule()],
                    },
                  },
                ]}
              />
            </Col>
          </Row>
        </Flex>
        <Flex vertical gap={0}>
          <PageTitle
            title="Update and cancel"
            level={4}
            style={{ margin: 0 }}
          />
          <Divider />
          <Row gutter={[32, 32]}>
            <Col span={12}>
              <FormCard
                title="Modification cut off time"
                icon={<ClockCircleOutlined />}
                entity={Entity.setting}
                action={SBRMAction.Update}
                onSuccess={fetchSettings}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.BOOKING_IS_UPDATABLE_TIME,
                  },
                ]}
                footer={
                  <Alert
                    type={updateCutOff?.value == 0 ? 'warning' : 'info'}
                    title={
                      updateCutOff?.value == 0
                        ? 'With current setting, reservations cannot be updated'
                        : `With current setting, future reservations can be updated up to ${updateCutOff?.value} min before their time`
                    }
                  />
                }
              />
            </Col>
            <Col span={12}>
              <FormCard
                title="Cancellation cut off time"
                footer={
                  <Alert
                    type={cancelCutOff?.value == 0 ? 'warning' : 'info'}
                    title={
                      cancelCutOff?.value == 0
                        ? 'With current setting, reservations cannot be canceled'
                        : `With current setting, future reservations can be canceled up to ${cancelCutOff?.value} min before their time`
                    }
                  />
                }
                icon={<ClockCircleOutlined />}
                entity={Entity.setting}
                action={SBRMAction.Update}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.BOOKING_IS_CANCELLABLE_TIME,
                  },
                ]}
                onSuccess={fetchSettings}
              />
            </Col>
          </Row>
        </Flex>
      </Flex>
    </Page>
  )
}

export default BookingSettingsPage
